import requiredEnvVars from './requiredEnv.json';

const DEFAULT_SHARE_ROUTE = '/share';

// Shared with scripts/check-env.js, which fails `npm run build` when any are missing.
export const REQUIRED_ENV_VARS = requiredEnvVars;

const getWindowOrigin = () => (typeof window !== 'undefined' ? window.location.origin : '');

const getRedirectUri = (publicUrl) => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return new URL(publicUrl || '/', window.location.origin).href.replace(/\/$/, '');
};

const normalizeShareRoutePrefix = (value) => {
  if (!value || typeof value !== 'string') {
    return DEFAULT_SHARE_ROUTE;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return DEFAULT_SHARE_ROUTE;
  }

  const ensuredLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const withoutTrailingSlash = ensuredLeadingSlash.replace(/\/+$/, '');
  return withoutTrailingSlash || DEFAULT_SHARE_ROUTE;
};

const readEnv = (env, name) => (typeof env[name] === 'string' ? env[name].trim() : '');

// Feature flags are off unless their variable is exactly "true". To add one, list its
// REACT_APP_FEATURE_* variable here and in the env object passed to buildConfig below.
export const FEATURE_FLAG_ENV_VARS = {
  exportProfile: 'REACT_APP_FEATURE_EXPORT_PROFILE'
};

const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

export const parseFeatureFlag =(value) => typeof value === 'string' && value.trim() === 'true';

const buildFeatures = (env) =>
  Object.fromEntries(
    Object.entries(FEATURE_FLAG_ENV_VARS).map(([feature, name]) => [feature, parseFeatureFlag(env[name])])
  );

// Builds runtime config from REACT_APP_* variables. Missing required variables are
// reported (by name only) rather than replaced with hardcoded fallbacks.
export const buildConfig = (env) => {
  const missing = REQUIRED_ENV_VARS.filter((name) => !readEnv(env, name));
  const region = readEnv(env, 'REACT_APP_COGNITO_REGION');
  const userPoolId = readEnv(env, 'REACT_APP_COGNITO_USER_POOL_ID');

  return {
    missing,
    config: {
      auth: {
        authority: region && userPoolId ? `https://cognito-idp.${region}.amazonaws.com/${userPoolId}` : '',
        clientId: readEnv(env, 'REACT_APP_COGNITO_CLIENT_ID'),
        redirectUri: getRedirectUri(env.PUBLIC_URL),
        responseType: 'code',
        scope: 'email openid phone profile',
        logoutDomain: readEnv(env, 'REACT_APP_COGNITO_DOMAIN').replace(/\/+$/, ''),
        logoutUri: readEnv(env, 'REACT_APP_COGNITO_LOGOUT_URI') || `${getWindowOrigin()}/logout`
      },
      api: {
        fastApiBaseUrl: readEnv(env, 'REACT_APP_FASTAPI_BASE_URL').replace(/\/+$/, ''),
        shareRoutePrefix: normalizeShareRoutePrefix(env.REACT_APP_SHARE_LINK_ROUTE_PREFIX)
      },
      features: buildFeatures(env),
      analytics: {
        // Google Analytics 4 measurement ID (e.g. G-ABC123). Unset or invalid = analytics off.
        measurementId: GA_MEASUREMENT_ID_PATTERN.test(readEnv(env, 'REACT_APP_GA_MEASUREMENT_ID'))
          ? readEnv(env, 'REACT_APP_GA_MEASUREMENT_ID')
          : ''
      }
    }
  };
};

// CRA only inlines process.env.REACT_APP_* when referenced statically, so list them explicitly.
const built = buildConfig({
  PUBLIC_URL: process.env.PUBLIC_URL,
  REACT_APP_FASTAPI_BASE_URL: process.env.REACT_APP_FASTAPI_BASE_URL,
  REACT_APP_COGNITO_REGION: process.env.REACT_APP_COGNITO_REGION,
  REACT_APP_COGNITO_USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  REACT_APP_COGNITO_CLIENT_ID: process.env.REACT_APP_COGNITO_CLIENT_ID,
  REACT_APP_COGNITO_DOMAIN: process.env.REACT_APP_COGNITO_DOMAIN,
  REACT_APP_COGNITO_LOGOUT_URI: process.env.REACT_APP_COGNITO_LOGOUT_URI,
  REACT_APP_SHARE_LINK_ROUTE_PREFIX: process.env.REACT_APP_SHARE_LINK_ROUTE_PREFIX,
  REACT_APP_FEATURE_EXPORT_PROFILE: process.env.REACT_APP_FEATURE_EXPORT_PROFILE,
  REACT_APP_GA_MEASUREMENT_ID: process.env.REACT_APP_GA_MEASUREMENT_ID
});

export const config = built.config;
export const MISSING_CONFIG = built.missing;
export const FEATURES = config.features;

export const FAST_API_BASE_PEOPLE = `${config.api.fastApiBaseUrl}/people`;
export const FAST_API_BASE_STATS = `${config.api.fastApiBaseUrl}/stats`;
export const FAST_API_BASE_SKILLS = `${config.api.fastApiBaseUrl}/skills`;
export const FAST_API_BASE_SHARING_LINKS = `${config.api.fastApiBaseUrl}/sharinglinks`;
export const SHARE_LINK_ROUTE_PREFIX = config.api.shareRoutePrefix;

export const buildShareLinkPath = (token) => {
  if (!token) {
    return null;
  }
  const safeToken = encodeURIComponent(token);
  return `${SHARE_LINK_ROUTE_PREFIX}/${safeToken}`;
};

export const buildShareLinkUrl = (token, originOverride) => {
  const path = buildShareLinkPath(token);
  if (!path) {
    return null;
  }

  const resolvedOrigin = originOverride ?? getWindowOrigin();
  return resolvedOrigin ? `${resolvedOrigin}${path}` : path;
};
